import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from "react-router";
import {useTranslation} from 'react-i18next';
import {AxiosError} from 'axios';
import {Location} from 'history';
import client from 'lib/CommonApi/client';
import {
  callAlertWeb,
  callNativeWeb,
  clickMoveToAppWeb,
  footerShowEventWeb,
  hideProgressWeb,
  showProgressWeb
} from 'common/appInterface';
import { CardProductCode, CardProductTabName, QUICK_CARD_REGISTER_MIN_VERSION } from '../../constants/Card';
import { WEBVIEW_RESOURCE_VERSION } from 'common/cacheOverwrite';
import BottomModal from 'components/BottomModal/BottomModal';
import StringUtils from 'common/stringUtils';

const BenefitsTabPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Params */
  const { cardProductCode = "" } = useParams();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const screenId = "APPL02";
  const screenName = t("APPL02.title");

  const [productList, setProdcutList] = useState<Map<string, Product>>();

  /** 카드상품 조회 */
  const callProductList = useCallback(async () => {
    showProgressWeb();
    await client.get("/api/app/product", { params: location.state.query }).then(response => {
      let productMap = new Map<string, Product>();
      response.data.response.filter((value: Product) => value.useYn === "Y" || value.hasYn === "Y" || value.receiveYn === "Y").forEach((value: Product) => {
        productMap.set(value.cardProductCode, value);
      });
      setProdcutList(productMap);
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
    });
  }, [location]);

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    // callNativeWeb(screenId, screenName);
    callNativeWeb(screenId, screenName, location.state.apply.linkOpenMenu === "benefitsTab" ? 'Y' : 'N');
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);

    callProductList();

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {productList && <BenefitsTabChild productList={productList} cardProductCode={cardProductCode} />}
    </>
  );
};

const BenefitsTabChild: React.FC<{ productList: Map<string, Product>, cardProductCode: string}> = (props) => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t } = useTranslation();

  const [cardApply, setCardApply] = useState<Partial<CardApplyStep2>>({ ...location.state.apply });
  const [cardProductCode, setCardProductCode] = useState(props.cardProductCode);

  const [debitState, setDebitState] = useState<CardDebitState | null>(null);
  const [isUpgrade, setIsUpgrade] = useState(false);
  const [isUpgradeModalView, setIsUpgradeModalView] = useState<boolean>(false);

  const [reissueFee, setReissueFee] = useState(0);
  const [reissuePromotionFee, setReissuePromotionFee] = useState(0);
  const [promotionRate, setPromotionRate] = useState(0);

  // 카드 업그레이드 모달 닫기
  const closeUpgradeModal = useCallback(() => {
    setIsUpgradeModalView(false);
  }, []);

  /** 출금동의여부조회 */
  const callDebitState = useCallback(async () => {
    await client.get('/api/app/manage/debit/state', {
      params: {
        ...location.state.query
      }
    }).then(response => {
      setDebitState(response.data.response);
    }).catch((e: AxiosError) => {
      callAlertWeb('ERROR', '99', e.message);
    });
  }, [location.state.query]);

  // 카드 업그레이드 클릭
  const OnUpgradeCardClick = useCallback(() => {
    navigate('/apply/cardDesignSelect', {
      state: {
        ...location.state,
        query: {
          ...location.state.query,
          cardProductCode: cardApply.cardProductCode
        },
        apply: cardApply,
        userInfo: debitState
      }
    });
  }, [location.state, navigate, debitState, cardApply]);

  /** backKeyEvent_App 처리 (APP > WEB) */
  const callbackBackkeyEvent = useCallback(() => {
    if (isUpgradeModalView) {
      closeUpgradeModal();
    } else {
      navigate(-1);
    }

  }, [navigate, closeUpgradeModal, isUpgradeModalView]);

  /** 카드신청 클릭 */
  const clickApply = useCallback(() => {
    switch (Number(cardProductCode)) {
      case CardProductCode.QuickCard:
        if (Number(location.state.query.apiVersion) >= QUICK_CARD_REGISTER_MIN_VERSION) {
          clickMoveToAppWeb("N876");
        } else {
          callAlertWeb("99", "99", t('STAT01.popup05'));
        }
        break;
      default:
        showProgressWeb();

        /** 고객정보 조회 */
        client.get("/api/app/apply/info", {
          params: {
            ...location.state.query,
            cardProductCode: cardApply.cardProductCode
          }
        }).then((response) => {
          let result = response.data.response;

          if (response.data.code === "00") {
            // KYC 미인증
            if (result.kycApproved === "N") {
              callAlertWeb("ERROR", "99", t("APPL02.kycApproved"));
            }
            else {
              navigate("/apply/step0", {
                state: {
                  ...location.state,
                  query: {
                    ...location.state.query,
                    cardProductCode: cardApply.cardProductCode
                  },
                  apply: cardApply
                }
              });
            }
          }
          else {
            callAlertWeb("ERROR", response.data.code, response.data.message);
          }
        }).catch((e: AxiosError) => {
          callAlertWeb("ERROR", "99", e.message);
        }).finally(() => {
          hideProgressWeb();
        });
        break;
    }
  }, [location, navigate, cardApply, t, cardProductCode]);

  // APP이벤트 공통 수신처리 (APP > WEB)
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  useEffect(() => {
    callDebitState();
  }, [callDebitState]);

  const inquiryMyCardInfoApi = useCallback(
    async () => {

      let isUpgradeAvailable = false;

      let cardInfo: any = null;

      showProgressWeb();
      setIsUpgradeModalView(false);
      setIsUpgrade(false);
      await client.get(`/api/app/product/${cardProductCode}/my`, { params: location.state.query }).then((response) => {
        if(response?.data?.response) {
          cardInfo = response?.data?.response;

          if (cardInfo && cardInfo.cardStatusCode) {
            if ((cardInfo.cardStatusCode === "11" || cardInfo.cardStatusCode === "22") && (cardInfo.designDivisionCode === "A" && cardInfo.reissueReceiveYn === "N")) {
              isUpgradeAvailable = true;
            }
          }
        }

        if (isUpgradeAvailable) {
          setReissueFee(cardInfo.reissueFee);
          setPromotionRate(cardInfo.promotionRate);
          setReissuePromotionFee(cardInfo.reissuePromotionFee);

          setIsUpgradeModalView(true);
          setIsUpgrade(true);

          setCardApply({
            ...cardApply,
            cardProductCode: cardProductCode,
            beforeAlternativeCardNo: cardInfo.alternativeCardNo,
            reissueReceiveYn: cardInfo.reissueReceiveYn,
            reissueFee: cardInfo.reissueFee,
            promotionRate: cardInfo.promotionRate,
            reissuePromotionFee: cardInfo.reissuePromotionFee,
            requestIssueDivisionCode: cardInfo.cardStatusCode === "22" ? "24" : "21",
            isReissue: true,
            reissuePromotionYn: "Y"
          });
        } else {
          setIsUpgradeModalView(false);
          setIsUpgrade(false);

          setCardApply({
            ...cardApply,
            cardProductCode: cardProductCode,
            beforeAlternativeCardNo: undefined,
            reissueReceiveYn: undefined,
            reissueFee: undefined,
            isReissue: undefined,
            promotionRate: undefined,
            reissuePromotionFee: undefined,
            reissuePromotionYn: undefined,
            requestIssueDivisionCode: location.state.cardInfoListLength > 0 ? "12" : "11",
          });
        }
      }).catch((e: AxiosError) => {
        callAlertWeb("ERROR", "99", e.message);
      }).finally(() => {
        hideProgressWeb();
      });
      // eslint-disable-next-line
    }, [cardProductCode, location.state.query, location.state.cardInfoListLength]);

  useEffect(() => {
    setCardApply((value) => ({ ...value, cardProductCode: cardProductCode }));
    inquiryMyCardInfoApi();
  }, [cardProductCode, inquiryMyCardInfoApi]);

  useEffect(() => {
    console.log("setIsUpgradeModalView 변경");
    console.log(setIsUpgradeModalView);
  }, [setIsUpgradeModalView]);

  return (
    <div id="wrap">
      <div id="container" className="pcHidden">
        <section className="contents">
          <div className="tabWrap">
            <div className="btnTabWrap">
              {Array.from(props.productList.values()).map((value) => (
                <div
                  key={value.cardProductCode}
                  className={
                    cardProductCode === value.cardProductCode
                      ? 'btnTab on'
                      : 'btnTab'
                  }
                  onClick={() => setCardProductCode(value.cardProductCode)}>
                  <button type="button">
                    <span>
                      {CardProductTabName[Number(value.cardProductCode)]}
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="inner">
            <div className="section serverWrap">
              <iframe
                className="productTabDetail"
                key={cardProductCode}
                src={`${process.env.REACT_APP_OUTER_HTML_PATH}/card/cardhtml/html/${location.state.query.languageCode}/${cardProductCode}_benefit.html?v=${WEBVIEW_RESOURCE_VERSION}`}
                allowFullScreen={true}
                title={
                  props.productList?.get(cardProductCode)?.cardProductCodeName
                }
              />
            </div>
          </div>
        </section>
        <div className="btnArea">
          <div className="btnWrapTy01">
            {isUpgrade && (
              <button type="button" className="btnPrimaryTy01"
                      onClick={OnUpgradeCardClick}>
                <span>{t('APPL02.upgradeCard', 'Upgrade Card')}</span>
              </button>
            )}
            {!isUpgrade && (
              <button
                type="button"
                className="btnPrimaryTy01"
                onClick={clickApply}
                disabled={
                  !props.productList ||
                  props.productList?.get(cardProductCode)?.hasYn === 'Y' ||
                  props.productList?.get(cardProductCode)?.receiveYn === 'Y' ||
                  !Array.from(props.productList?.keys()).includes(cardProductCode)
                }>
              <span>
                {props.productList?.get(cardProductCode)?.hasYn === 'Y'
                  ? t('APPL02.exist')
                  : props.productList?.get(cardProductCode)?.receiveYn === 'Y'
                  ? t('APPL02.receive')
                  : t('APPL02.apply')}
              </span>
              </button>
            )}
          </div>
        </div>
      </div>
      <BottomModal isOpen={isUpgradeModalView} onClose={closeUpgradeModal}
                   actionLabel={t('APPL02.upgradeCard', 'Upgrade Card')} onAction={OnUpgradeCardClick}>
        <div className={'upgradeBottomModalContainer'}>
          <div className={'upgradeBottomModalTitle'}>
            <span>{t('APPL02.upgradeTitle', 'Issuing Fee Discount')} {promotionRate}%</span>
          </div>
          <div className={'upgradeBottomModalDescription'}>
            <img className={'upgradeNoticeImage'} src={'/assets/images/ico/notice.png'} alt={'Notice Icon'} />
            <span>{t('APPL02.upgradeDescription', 'Any ATM Cash Deposit Available (Including all bank & CVS ATM)')}</span>
          </div>
          <div className={'upgradeBottomModalDescription'}>
            <img className={'upgradeNoticeImage'} src={'/assets/images/ico/notice.png'} alt={'Notice Icon'} />
            <span className={'upgradeBottomModalOldFeeText'}>{StringUtils.moneyFormat(StringUtils.nvlDefault(reissueFee, '0'))} KRW</span>
            <span className={'upgradeBottomModalFeeText'}>{StringUtils.moneyFormat(StringUtils.nvlDefault(reissuePromotionFee, '0'))} KRW</span>
          </div>
        </div>
      </BottomModal>
    </div>
  );
};

export default BenefitsTabPage;